import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Navigate, Route, Routes } from "react-router-dom";
import AppLayout from "./layouts/AppLayout";
import Home from "./components/Home";
import Settings from "./components/Settings";
import Cloud9 from "./components/Cloud9";
import Profile from "./components/Profile";
import Notifications from "./components/Notifications";
import Login from "./components/Login";
import PublicProfile from "./components/PublicProfile";
import AdminReset from "./components/AdminReset";
import AdminTools from "./components/AdminTools";
import PostPage from "./components/PostPage";
import Communities from "./components/Communities";
import Help from "./components/Help";
import AskScoldy from "./components/AskScoldy";

function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const restoreAuthenticatedUser = async () => {
      let savedUser = null;

      try {
        const rawSavedUser = localStorage.getItem("currentUser");
        savedUser = rawSavedUser ? JSON.parse(rawSavedUser) : null;
      } catch {
        localStorage.removeItem("currentUser");
        localStorage.removeItem("groupLimits");
        localStorage.removeItem("isGuest");
      }

      // Guest access is intentionally local and does not have an auth_sessions row.
      if (savedUser?.isGuest === true || Number(savedUser?.group_id) === 0) {
        if (!cancelled) {
          setCurrentUser(savedUser);
          setAuthChecked(true);
        }
        return;
      }

      // No cached signed-in user means there is nothing to restore.
      if (!savedUser) {
        if (!cancelled) {
          setCurrentUser(null);
          setAuthChecked(true);
        }
        return;
      }

      try {
        // The server session is authoritative. This prevents stale localStorage
        // from making Scoldy appear logged in after the HttpOnly session cookie
        // has expired, been revoked, or otherwise become invalid.
        const response = await fetch("/backend/auth/me", {
          method: "GET",
          credentials: "include",
          cache: "no-store",
        });

        if (!response.ok) {
          localStorage.removeItem("currentUser");
          localStorage.removeItem("groupLimits");
          localStorage.removeItem("isGuest");

          if (!cancelled) {
            setCurrentUser(null);
          }
          return;
        }

        const result = await response.json();
        const authenticatedUser = result?.user || null;

        if (!authenticatedUser) {
          localStorage.removeItem("currentUser");
          localStorage.removeItem("groupLimits");
          localStorage.removeItem("isGuest");

          if (!cancelled) {
            setCurrentUser(null);
          }
          return;
        }

        // Refresh the existing UI cache from the authenticated server identity.
        localStorage.setItem("currentUser", JSON.stringify(authenticatedUser));
        localStorage.removeItem("isGuest");

        if (!cancelled) {
          setCurrentUser(authenticatedUser);
        }
      } catch (error) {
        // A network/server outage is not proof that the user's session is invalid.
        // Preserve the existing cache rather than logging the user out incorrectly.
        console.error("Unable to verify the existing Scoldy session:", error);

        if (!cancelled) {
          setCurrentUser(savedUser);
        }
      } finally {
        if (!cancelled) {
          setAuthChecked(true);
        }
      }
    };

    restoreAuthenticatedUser();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleLogin = (user) => {
    localStorage.setItem("currentUser", JSON.stringify(user));
    setCurrentUser(user);
  };

  const layout = (content) => <AppLayout>{content}</AppLayout>;

  if (!authChecked) {
    return null;
  }

  return (
    <Router>
      <Routes>
        <Route path="/cloud9" element={layout(<Cloud9 />)} />

        {!currentUser ? (
          <>
            <Route path="/login" element={<Login onLogin={handleLogin} />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </>
        ) : (
          <>
            <Route
              path="/admin-reset"
              element={layout(
                <AdminReset
                  adminId={currentUser.id}
                  adminGroup={currentUser.group_id}
                />
              )}
            />

            <Route
              path="/admin-tools"
              element={layout(
                <AdminTools
                  adminId={currentUser.id}
                  adminGroup={currentUser.group_id}
                />
              )}
            />

            <Route path="/" element={layout(<Home />)} />
            <Route path="/communities" element={layout(<Communities />)} />
            <Route path="/settings" element={layout(<Settings />)} />
            <Route
              path="/profile"
              element={layout(<Profile setCurrentUser={setCurrentUser} />)}
            />
            <Route path="/profile/:username" element={layout(<PublicProfile />)} />
            <Route path="/notifications" element={layout(<Notifications />)} />
            <Route path="/help" element={layout(<Help />)} />
            <Route path="/ask-scoldy" element={layout(<AskScoldy />)} />
            <Route
              path="/post/:id"
              element={layout(<PostPage currentUser={currentUser} />)}
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </>
        )}
      </Routes>
    </Router>
  );
}

export default App;
